import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { User, UserRole } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { EditUserModal } from '../../components/admin/EditUserModal';
import { CreateUserModal } from '../../components/admin/CreateUserModal';
import { ResetPasswordModal } from '../../components/admin/ResetPasswordModal';
import { useAuthStore } from '../../stores/authStore';

interface UserListResponse {
  items: User[];
  total: number;
  totalPages: number;
}

const STAFF_ROLES: UserRole[] = ['store_manager', 'admin', 'super_admin'];

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const secondaryBtn =
  'rounded-md border border-admin-border bg-white px-3 py-1.5 text-sm hover:bg-admin-surface-alt disabled:cursor-not-allowed disabled:opacity-40';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover';
const thClass =
  'p-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-muted';

export default function AdminEquipaPage(): JSX.Element {
  const [sp, setSp] = useSearchParams();
  const currentRole = useAuthStore((s) => s.user?.role);
  const isSuperAdmin = currentRole === 'super_admin';
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState<User | null>(null);
  const [resetting, setResetting] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);

  const load = async (): Promise<void> => {
    const params = new URLSearchParams(sp);
    params.set('scope', 'staff');
    if (!params.get('page')) params.set('page', '1');
    if (!params.get('limit')) params.set('limit', '25');
    const response = await api.get<UserListResponse>(`/admin/users?${params.toString()}`);
    setUsers(response.data.items);
    setTotal(response.data.total);
  };

  useEffect(() => {
    void load();
  }, [sp]);

  const setRole = async (id: string, role: UserRole): Promise<void> => {
    await api.patch(`/admin/users/${id}`, { role });
    await load();
  };

  const toggleActive = async (u: User): Promise<void> => {
    await api.patch(`/admin/users/${u.id}`, { isActive: !u.isActive });
    await load();
  };

  return (
    <>
      <SEO title="Equipa (admin)" noindex />
      <div className="flex items-center justify-between">
        <h1>Equipa ({total})</h1>
        <button type="button" onClick={() => setCreating(true)} className={primaryBtn}>
          + Novo utilizador
        </button>
      </div>
      <input
        type="search"
        placeholder="Pesquisar…"
        value={sp.get('search') ?? ''}
        onChange={(e) => {
          const next = new URLSearchParams(sp);
          if (e.target.value) next.set('search', e.target.value);
          else next.delete('search');
          next.set('page', '1');
          setSp(next);
        }}
        className={`${inputClass} my-4 w-80`}
      />
      <table className="overflow-hidden rounded-lg bg-admin-surface shadow-sm">
        <thead>
          <tr className="border-b border-admin-border">
            <th className={thClass}>Email</th>
            <th className={thClass}>Nome</th>
            <th className={thClass}>Papel</th>
            <th className={thClass}>Criado</th>
            <th className={thClass}></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            // Only a super_admin may manage another super_admin account.
            const locked = u.role === 'super_admin' && !isSuperAdmin;
            // A non-super_admin can't grant the super_admin role either.
            const roleOptions = isSuperAdmin
              ? STAFF_ROLES
              : STAFF_ROLES.filter((r) => r !== 'super_admin');
            return (
            <tr
              key={u.id}
              className="border-b border-admin-border last:border-b-0 hover:bg-admin-surface-alt"
            >
              <td className="p-3 font-medium">{u.email}</td>
              <td className="p-3">
                {u.firstName} {u.lastName}
              </td>
              <td className="p-3">
                <select
                  value={u.role}
                  disabled={locked}
                  onChange={(e) => {
                    void setRole(u.id, e.target.value as UserRole);
                  }}
                  className={`${inputClass} disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  {roleOptions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </td>
              <td className="p-3 text-admin-text-soft">
                {formatDate(u.createdAt)}
              </td>
              <td className="p-3 text-right">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(u)}
                    disabled={locked}
                    title={locked ? 'Apenas super_admin pode gerir contas super_admin' : undefined}
                    className={secondaryBtn}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void toggleActive(u);
                    }}
                    disabled={locked}
                    title={locked ? 'Apenas super_admin pode gerir contas super_admin' : undefined}
                    className={secondaryBtn}
                  >
                    {u.isActive ? 'Desativar' : 'Ativar'}
                  </button>
                  {isSuperAdmin ? (
                    <button
                      type="button"
                      onClick={() => setResetting(u)}
                      className={secondaryBtn}
                    >
                      Repor palavra-passe
                    </button>
                  ) : null}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>

      {editing ? (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}

      {resetting ? (
        <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />
      ) : null}

      {creating ? (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void load();
          }}
        />
      ) : null}
    </>
  );
}
